import { select, takeEvery } from 'redux-saga/effects';

import { userIdSelector } from '@/domain/env/envSelector';
import * as A from '@/domain/documents/gridPreset/gridPresetActions';
import {
  gridPresetChangesSelector,
  gridPresetCompanyIdSelector,
  gridPresetCurrentSelector,
  gridPresetPersistableSelector,
  gridPresetIdSelector,
  gridPresetSharedIdSelector,
} from '@/domain/documents/gridPreset/gridPresetSelectors';
import { writeStoredPreset } from '@/domain/documents/gridPreset/persistence';
import { hasLayoutChanges } from '@/domain/documents/gridPreset/convert';
import type { GridLayout, GridLayoutChanges } from '@/domain/documents/gridPreset/schemas';
import { browserStorage } from '@/lib/storage';

// Every slice action but `layoutCleared` may leave a layout worth keeping; the slice says whether it may be stored
// (`persistable`). Synchronous and cheap; the grid reports once per user gesture, so no debounce.
const PERSISTED_ACTIONS = [
  A.gridPresetResolvedAction.type,
  A.gridPresetChosenAction.type,
  A.gridPresetResetAction.type,
  A.gridPresetFiltersSetAction.type,
  A.gridPresetGridStateReportedAction.type,
  A.gridPresetSavedAction.type,
  A.gridPresetDefaultColumnsChangedAction.type,
];

export function* persistGridPreset(storage: Storage) {
  const persistable: boolean = yield select(gridPresetPersistableSelector);
  if (!persistable) return;
  const userId: unknown = yield select(userIdSelector);
  const companyId: string | null = yield select(gridPresetCompanyIdSelector);
  const presetId: string = yield select(gridPresetIdSelector);
  const sharedPresetId: string | null = yield select(gridPresetSharedIdSelector);
  const current: GridLayout | null = yield select(gridPresetCurrentSelector);
  const changes: GridLayoutChanges = yield select(gridPresetChangesSelector);

  if (!userId || !companyId || !current) return;
  // A full or blocked storage (QuotaExceededError, private mode) must not escape: it would end the watcher and, through
  // the root saga, every other one. The layout then lives only in the store.
  try {
    writeStoredPreset(storage, String(userId), companyId, {
      presetId,
      sharedPresetId,
      ...(hasLayoutChanges(changes) && { changes }),
    });
  } catch {
    // Nothing to recover: the next change tries again.
  }
}

export function* watchGridPresetPersistence() {
  yield takeEvery(PERSISTED_ACTIONS, persistGridPreset, browserStorage());
}
